import axios from 'axios';

/** Single localStorage key for the portal JWT (must match AuthContext login/register/logout). */
export const ACCESS_TOKEN_STORAGE_KEY = 'access_token';

/**
 * Reads JWT from common login/register response shapes.
 * Covers: `access_token`, `accessToken`, `token`, `jwt`, and nested `data.*` (e.g. `data.access_token`).
 * @param {unknown} data — `response.data` from POST /account/login or /account/register
 * @returns {string|null}
 */
export function extractAccessTokenFromAuthResponse(data) {
  if (!data || typeof data !== 'object') return null;
  const nested = data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : null;
  const candidates = [
    data.access_token,
    data.accessToken,
    data.token,
    data.jwt,
    nested?.access_token,
    nested?.accessToken,
    nested?.token,
    nested?.jwt,
  ];
  for (const v of candidates) {
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) return t;
    }
  }
  return null;
}

/**
 * User object from login/register JSON (handles common gateway wrappers).
 * @param {unknown} data — `response.data` from POST /account/login or /account/register
 * @returns {Record<string, unknown>|null}
 */
export function extractUserFromAuthResponse(data) {
  if (!data || typeof data !== 'object') return null;
  const nested = data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : null;
  const candidates = [data.user, nested?.user, data.profile, nested?.profile];
  for (const c of candidates) {
    if (c && typeof c === 'object' && !Array.isArray(c)) {
      return { ...c };
    }
  }
  return null;
}

/**
 * Decode JWT payload (middle segment) without verifying the signature (browser only).
 * Lets the UI recover `role` when login/`me` JSON is stripped by a proxy. APIs still validate the token server-side.
 * @param {string} token
 * @returns {Record<string, unknown>|null}
 */
export function readJwtPayloadUnsafe(token) {
  if (typeof token !== 'string' || !token.includes('.')) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(padLen);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

const rawBackend = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
const baseURL = rawBackend.endsWith('/api') ? rawBackend : `${rawBackend}/api`;

const api = axios.create({
  baseURL,
  timeout: 25000,
  withCredentials: true,
});

/**
 * Mirror `localStorage` `access_token` into axios default headers so the next request
 * (e.g. GET /account/me right after login) always sends Authorization + X-Access-Token.
 */
export function syncAuthHeadersFromStorage() {
  let raw = null;
  try {
    raw = typeof localStorage !== 'undefined' ? localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) : null;
  } catch {
    raw = null;
  }
  const token = typeof raw === 'string' ? raw.trim() : '';
  if (token) {
    const bearer = `Bearer ${token}`;
    api.defaults.headers.common.Authorization = bearer;
    api.defaults.headers.common['X-Access-Token'] = token;
  } else {
    delete api.defaults.headers.common.Authorization;
    delete api.defaults.headers.common['X-Access-Token'];
  }
}

// Attach portal JWT on every request (defaults + per-request for safety after runtime token changes)
api.interceptors.request.use((config) => {
  let raw = null;
  try {
    raw = typeof localStorage !== 'undefined' ? localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) : null;
  } catch {
    raw = null;
  }
  const token = typeof raw === 'string' ? raw.trim() : '';
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-Access-Token'] = token;
  } else {
    delete config.headers.Authorization;
    delete config.headers['X-Access-Token'];
  }
  return config;
});

syncAuthHeadersFromStorage();

export default api;
